import * as yup from "yup";
import React, { FC, useState } from "react";
import { Block } from "kanopi-components/Block";
import { Button, KIND, TButtonProps } from "kanopi-components/Button";
import { CardBody } from "kanopi-components/Card";
import { Form } from "kanopi-components/Form";
import { FormControl, Input } from "kanopi-components/Input";
import { HeadingSmall } from "kanopi-components/typography";
import { Modal } from "kanopi-components/Modal";
import { toaster } from "kanopi-components/ToastContainer";
import { useAPI } from "kw/api/useAPI";
import { useForm } from "react-weaver";

type TAdminEmailQuoteModal = {
  quote: TQuote;
  openModal: (boolean) => void;
};

const AdminEmailQuoteModal: FC<TAdminEmailQuoteModal> = ({ quote, openModal }) => {
  const { mutation } = useAPI();
  const initialFormValues = {
    email: quote.riskProfile.owner.email || quote.riskProfile.owner.contactEmail || "",
  };

  const { fieldProps, errors, values, notReady } = useForm({
    validator: yup.object().shape({
      email: yup.string().email().required().label("Email"),
    }),
    initialValues: initialFormValues,
  });

  const sendQuote = () => {
    mutation(
      {
        admin: {
          emailQuote: [
            {
              input: {
                quoteId: quote.id,
                toEmail: values.email,
              },
            },
            {
              message: true,
            },
          ],
        },
      },
      (response: { admin: { emailQuote: { message: string } } }) => {
        openModal(false);
        toaster({
          kind: "POSITIVE",
          content: response.admin.emailQuote.message,
          key: "send-quote-success-toast",
        });
      }
    );
  };

  const handleSubmit = async () => {
    sendQuote();
  };

  return (
    <Modal onClose={() => openModal(false)} closeable isOpen={true} animate autoFocus>
      <Modal.Body $style={{ marginTop: "64px" }}>
        <Form>
          <CardBody
            display="grid"
            gridGap="scale900"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingBottom="scale900"
            paddingTop="scale900"
          >
            <Block>
              <HeadingSmall margin={0}>Enter email address</HeadingSmall>
            </Block>
            <Block width="100%">
              <FormControl label="Email address" error={errors?.email}>
                <Input {...fieldProps.email} />
              </FormControl>
            </Block>
            <Button
              type="submit"
              kind={KIND.secondary}
              onClick={handleSubmit}
              disabled={notReady}
            >
              Send
            </Button>
          </CardBody>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

type TAdminEmailQuoteButton = {
  quote: TQuote;
} & TButtonProps;

export const AdminEmailQuoteButton: FC<TAdminEmailQuoteButton> = ({
  quote,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    quote?.riskProfile?.owner?.isActive && (
      <>
        <Button outlined={true} onClick={() => openModal(true)} {...props}>
          Email quote to customer
        </Button>

        {isOpen && <AdminEmailQuoteModal quote={quote} openModal={openModal} />}
      </>
    )
  );
};
